import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../components/common/LanguageSwitcher';
import LoginModal from '../components/auth/LoginModal';
import RegisterModal from '../components/auth/RegisterModal';
import ForgotPasswordModal from '../components/auth/ForgotPasswordModal';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../config/supabase';
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { ArrowRightIcon } from '../components/icons/NavIcons';
import { CoinIcon } from '../components/icons/NavIcons';
import { ProfileEditIcon } from '../components/icons/NavIcons';
import { MailIcon } from '../components/icons/NavIcons';
import { HandHeartIcon } from '../components/icons/NavIcons';
import { CopyrightIcon } from '../components/icons/NavIcons';
import { LegalIcon } from '../components/icons/NavIcons';
import { TermsIcon } from '../components/icons/NavIcons';
import { SmartphoneIcon } from '../components/icons/NavIcons';
import { SmartphoneAppIcon } from '../components/icons/NavIcons';
import { PrivacyIcon } from '../components/icons/NavIcons';
import { InvoiceIcon } from '../components/icons/NavIcons';

/**
 * Page des paramètres de l'application
 */
const SettingsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // Utilisation du contexte d'authentification
  const { user, loading: authLoading, signOut } = useAuth();
  const [userProfile, setUserProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [creditsBalance, setCreditsBalance] = useState(0);
  const [reportsCount, setReportsCount] = useState(0);
  const [pendingEventsCount, setPendingEventsCount] = useState(0);
  
  // États pour les modales d'authentification
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);

  // État pour l'installation PWA
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isSafari, setIsSafari] = useState(false);

  // Gestion des modales
  const handleOpenLoginModal = () => {
    setShowLoginModal(true);
    setShowRegisterModal(false);
    setShowForgotPasswordModal(false);
  };

  const handleOpenRegisterModal = () => {
    setShowRegisterModal(true);
    setShowLoginModal(false);
    setShowForgotPasswordModal(false);
  };

  const handleOpenForgotPasswordModal = () => {
    setShowForgotPasswordModal(true);
    setShowLoginModal(false);
    setShowRegisterModal(false);
  };

  const handleCloseAllModals = () => {
    setShowLoginModal(false);
    setShowRegisterModal(false);
    setShowForgotPasswordModal(false);
  };

  // Fonction de déconnexion
  const handleLogout = async () => {
    await signOut();
  };

  // Récupération du profil utilisateur depuis la table profiles
  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!user) return;
      
      setProfileLoading(true);
      try {
        const { data, error } = await supabase
          .from('roles')
          .select('*')
          .eq('id', user.id)
          .single();

        if (error) throw error;
        setUserProfile(data);
      } catch (error) {
        console.error('Erreur lors du chargement du profil:', error);
      } finally {
        setProfileLoading(false);
      }
    };
    
    fetchUserProfile();
  }, [user]);

  // Récupération des crédits depuis la table credits
  useEffect(() => {
    const fetchCredits = async () => {
      if (!user) return;
      
      try {
        const { data, error } = await supabase
          .from('credits')
          .select('credits')
          .eq('id', user.id)
          .single();

        if (error) {
          console.error('Erreur lors du chargement des crédits:', error);
          setCreditsBalance(0);
        } else {
          setCreditsBalance(data?.credits || 0);
        }
      } catch (error) {
        console.error('Erreur lors du chargement des crédits:', error);
        setCreditsBalance(0);
      }
    };
    
    fetchCredits();
  }, [user]);

  // Vérifier si l'utilisateur a des droits administratifs (admin ou admin_local)
  // const isAdmin = userProfile?.role === 'admin' || userProfile?.role === 'admin_local';
  const isAdmin = userProfile?.role === 'admin';

  const hasVerified = useRef(false);

  // Fonction pour rafraîchir le profil
  const refreshUserProfile = async () => {
    if (!user) return;
    
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (error) throw error;
      setUserProfile(data);
    } catch (error) {
      console.error('Erreur lors du rafraîchissement du profil:', error);
    }
  };

  // Fonction pour rafraîchir les crédits
  const refreshCredits = async () => {
    if (!user) return;
    
    try {
      const { data, error } = await supabase
        .from('credits')
        .select('credits')
        .eq('id', user.id)
        .single();

      if (error) {
        console.error('Erreur lors du rafraîchissement des crédits:', error);
        setCreditsBalance(0);
      } else {
        setCreditsBalance(data?.credits || 0);
      }
    } catch (error) {
      console.error('Erreur lors du rafraîchissement des crédits:', error);
      setCreditsBalance(0);
    }
  };

  useEffect(() => {
    const verifyPayment = async () => {
      const payment = searchParams.get('payment');
      const sessionId = searchParams.get('session_id');

      // Vérifier une seule fois
      if (payment === 'success' && sessionId && user && !hasVerified.current) {
        hasVerified.current = true;
        
        // Nettoyer l'URL immédiatement
        window.history.replaceState({}, '', '/settings');
        
        try {
          // Récupérer le token d'authentification
          const { data: { session } } = await supabase.auth.getSession();
          
          if (!session) {
            console.error('❌ Session non disponible');
            alert(t('credits.paymentVerificationError'));
            return;
          }

          // Utiliser la Edge Function Supabase
          const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
          const verifyUrl = `${supabaseUrl}/functions/v1/stripe-verify-payment`;

          console.log('🔍 Vérification du paiement:', { sessionId, userId: user.id });

          const response = await fetch(verifyUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${session.access_token}`,
            },
            body: JSON.stringify({
              sessionId,
              userId: user.id,
            }),
          });

          const data = await response.json();
          console.log('✅ Réponse de vérification:', data);

          if (response.ok && data.success) {
            // Rafraîchir le profil et les crédits pour obtenir le nouveau solde
            await refreshUserProfile();
            await refreshCredits();
            
            if (data.alreadyProcessed) {
              alert(t('credits.paymentAlreadyProcessed'));
            } else {
              alert(t('credits.paymentSuccess', { credits: data.creditsAdded }));
            }
          } else {
            console.error('❌ Erreur lors de la vérification:', data.error);
            alert(t('credits.paymentVerificationError'));
          }
        } catch (error) {
          console.error('❌ Erreur lors de la vérification du paiement:', error);
          alert(t('credits.paymentVerificationError'));
        }
      }
    };

    if (user) {
      verifyPayment();
    }
  }, [user, searchParams, t]);

  // Récupérer le nombre de signalements en attente
  useEffect(() => {
    const fetchReportsCount = async () => {
      if (!isAdmin) return;
      
      try {
        const { data: reportsData, error } = await supabase
          .from('reports')
          .select('event_id', { count: 'exact', head: false })
          .eq('status', 'pending');

        if (error) throw error;

        // Filtrer les événements publiés uniquement
        if (reportsData && reportsData.length > 0) {
          const eventIds = [...new Set(reportsData.map(r => r.event_id))];
          
          const { data: eventsData, error: eventsError } = await supabase
            .from('events')
            .select('id, status')
            .in('id', eventIds)
            .eq('status', 'published');

          if (eventsError) throw eventsError;

          // Compter le nombre d'événements publiés avec signalements
          const publishedEventIds = new Set(eventsData?.map(e => e.id) || []);
          const publishedReports = reportsData.filter(r => publishedEventIds.has(r.event_id));
          const uniqueEventIds = new Set(publishedReports.map(r => r.event_id));
          
          setReportsCount(uniqueEventIds.size);
        } else {
          setReportsCount(0);
        }
      } catch (error) {
        console.error('Erreur lors du chargement du nombre de signalements:', error);
        setReportsCount(0);
      }
    };

    fetchReportsCount();
  }, [isAdmin]);

  // Récupérer le nombre d'événements en attente d'approbation
  useEffect(() => {
    const fetchPendingEventsCount = async () => {
      if (!isAdmin) return;
      
      try {
        const { count, error } = await supabase
          .from('events')
          .select('*', { count: 'exact', head: true })
          .eq('status', 'pending_approval');

        if (error) throw error;
        setPendingEventsCount(count || 0);
      } catch (error) {
        console.error('Erreur lors du chargement du nombre d\'événements en attente:', error);
        setPendingEventsCount(0);
      }
    };

    fetchPendingEventsCount();
  }, [isAdmin]);

  // Détecter iOS et Safari
  useEffect(() => {
    const userAgent = window.navigator.userAgent.toLowerCase();
    const isIOSDevice = /iphone|ipad|ipod/.test(userAgent);
    const isSafariBrowser = /safari/.test(userAgent) && !/chrome/.test(userAgent);
    
    setIsIOS(isIOSDevice);
    setIsSafari(isSafariBrowser);
  }, []);

  // Vérifier si l'application est déjà installée
  useEffect(() => {
    // Vérifier si l'app tourne en mode standalone (déjà installée)
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches 
      || window.navigator.standalone 
      || document.referrer.includes('android-app://');
    
    setIsInstalled(isStandalone);
  }, []);

  // Gérer l'événement beforeinstallprompt pour l'installation PWA
  useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      console.log('beforeinstallprompt event fired');
      // Empêcher l'affichage automatique du prompt
      e.preventDefault();
      // Sauvegarder l'événement pour l'utiliser plus tard
      setDeferredPrompt(e);
    };

    const handleAppInstalled = () => {
      console.log('App installed');
      setIsInstalled(true);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  // Gérer le clic sur le bouton d'installation
  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      alert(t('settings.app.installNotAvailable'));
      return;
    }

    try {
      // Afficher le prompt d'installation
      await deferredPrompt.prompt();
      
      // Attendre le choix de l'utilisateur
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        console.log('Installation acceptée');
        alert(t('settings.app.installSuccess'));
      } else {
        console.log('Installation refusée');
      }
      
      // Réinitialiser le prompt
      setDeferredPrompt(null);
    } catch (error) {
      console.error('Erreur lors de l\'installation:', error);
      alert(t('settings.app.installError'));
    }
  };

  return (
    <div className="pb-16">
      <div className="p-4">
        {/* Section Compte */}
          <section className="mb-8">
            <h2 className="text-h2 mb-4">{t('settings.account.title')}</h2>
            
            {user ? (
              <>
                <div className="bg-white p-4 rounded-lg shadow-sm mb-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center">
                        <p className="font-medium">
                          {userProfile?.organizer_name || user?.user_metadata?.display_name || user.email}
                        </p>
                        {/* Badge d'administrateur avec texte traduit */}
                        {isAdmin && (
                          <span className="ml-2 px-2 py-1 text-xs font-semibold rounded-full bg-[#2894D8] text-white">
                            {userProfile?.role === 'admin_local' ? t('roles.adminLocal') : t('roles.admin')}
                          </span>
                        )}
                      </div>
                      <p className="text-gray-500 text-sm">{user.email}</p>
                    </div>
                  </div>
                </div>
              {/* Bouton dédié "Modifier mon profil organisateur" */}
              <Link to="/settings/profile" className="block bg-white p-4 rounded-lg shadow-sm mb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <ProfileEditIcon className="w-6 h-6 mr-3" />
                    <span>{t('settings.account.editOrganizerProfile')}</span>
                  </div>
                  <ArrowRightIcon className="w-5 h-5" />
                </div>
              </Link>

              <Link to="/settings/credits" className="block bg-white p-4 rounded-lg shadow-sm mb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <CoinIcon className="w-6 h-6 mr-3" />
                    <div className="flex flex-col">
                      <span>{t('settings.account.rechargeCredits')}</span>
                      {profileLoading ? (
                        <span className="text-sm text-gray-500">{t('common.loading')}...</span>
                      ) : (
                        <div className="flex items-center text-sm text-gray-500 mt-1">
                       
                          <span>{creditsBalance}</span>&nbsp;<CoinIcon className="w-4 h-4 mr-1" />
                        </div>
                      )}
                    </div>
                  </div>
                  <ArrowRightIcon className="w-5 h-5" />
                </div>
              </Link>

              <Link to="/settings/invoices" className="block bg-white p-4 rounded-lg shadow-sm mb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <InvoiceIcon className="w-6 h-6 mr-3" />
                    <span>{t('settings.account.invoices')}</span>
                  </div>
                  <ArrowRightIcon className="w-5 h-5" />
                </div>
              </Link>

              {/* <Link to="/settings/payment-methods" className="block bg-white p-4 rounded-lg shadow-sm mb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <span className="mr-3">💳</span>
                    <span>{t('settings.account.paymentMethods')}</span>
                  </div>
                  <ArrowRightIcon className="w-5 h-5" />
                </div>
              </Link>
              
              <Link to="/settings/billing" className="block bg-white p-4 rounded-lg shadow-sm mb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <span className="mr-3">📜</span>
                    <span>{t('settings.account.billingHistory')}</span>
                  </div>
                  <ArrowRightIcon className="w-5 h-5" />
                </div>
              </Link>
              
              <Link to="/settings/verify" className="block bg-white p-4 rounded-lg shadow-sm">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <span className="mr-3">✅</span>
                    <span>{t('settings.account.verifyAccount')}</span>
                  </div>
                  <ArrowRightIcon className="w-5 h-5" />
                </div>
              </Link> */}
              


              
              <button 
                className="w-full py-3 text-red-500 mt-6"
                onClick={handleLogout}
                disabled={authLoading}
              >
                {authLoading ? t('common.loading') : t('settings.account.logout')}
              </button>
            </>
          ) : (
            <div className="bg-white p-6 rounded-lg shadow-sm flex flex-col items-center">
              <p className="text-center mb-6">{t('settings.account.loginPrompt')}</p>
              <button 
                className="bg-[#2894D8] text-white py-2 px-6 rounded-lg mb-3 w-full"
                onClick={handleOpenLoginModal}
              >
                {t('common.login')}
              </button>
              <button 
                className="text-[#2894D8] py-2 w-full"
                onClick={handleOpenRegisterModal}
              >
                {t('common.register')}
              </button>
            </div>
          )}
        </section>
        
        {/* Section Administration */}
        {isAdmin && (
          <section className="mb-8">
            <h2 className="text-h2 mb-4">{t('settings.admin.title')}</h2>
            
            <div className="space-y-4">
              {/* Bouton Visualisation des visites */}
              <Link to="/admin/visits" className="block bg-white p-4 rounded-lg shadow-sm">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <span className="mr-3">👁️</span>
                    <span>{t('settings.admin.visits.title')}</span>
                  </div>
                  <ArrowRightIcon className="w-5 h-5" />
                </div>
              </Link>

              {/* Bouton Carte des intérêts */}
              <Link to="/admin/interests-map" className="block bg-white p-4 rounded-lg shadow-sm">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <span className="mr-3">📍</span>
                    <span>{t('settings.admin.interestsMap.title')}</span>
                  </div>
                  <ArrowRightIcon className="w-5 h-5" />
                </div>
              </Link>

              {/* Bouton Gestion des tâches d'importation */}
              <Link to="/admin/jobs-management" className="block bg-white p-4 rounded-lg shadow-sm mb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <span className="mr-3">🔄</span>
                    <span>{t('settings.account.manageJobs')}</span>
                  </div>
                  <ArrowRightIcon className="w-5 h-5" />
                </div>
              </Link>
              
              {/* Bouton Approbation des événements */}
              <Link to="/admin/event-approval" className="block bg-white p-4 rounded-lg shadow-sm mb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <span className="mr-3">✅</span>
                    <div className="flex items-center gap-2">
                      <span>{t('settings.admin.eventApproval.title')}</span>
                      {pendingEventsCount > 0 && (
                        <span className="px-2 py-1 bg-orange-500 text-white rounded-full text-xs font-bold">
                          {pendingEventsCount}
                        </span>
                      )}
                    </div>
                  </div>
                  <ArrowRightIcon className="w-5 h-5" />
                </div>
              </Link>

              {/* Bouton Signalements */}
              <Link to="/admin/reports" className="block bg-white p-4 rounded-lg shadow-sm">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <span className="mr-3">⚠️</span>
                    <div className="flex items-center gap-2">
                      <span>{t('settings.admin.reports.title')}</span>
                      {reportsCount > 0 && (
                        <span className="px-2 py-1 bg-red-500 text-white rounded-full text-xs font-bold">
                          {reportsCount}
                        </span>
                      )}
                    </div>
                  </div>
                  <ArrowRightIcon className="w-5 h-5" />
                </div>
              </Link>

              {/* Bouton Publication réseaux sociaux */}
              <Link to="/admin/social-publish" className="block bg-white p-4 rounded-lg shadow-sm mb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <span className="mr-3">📱</span>
                    <span>{t('settings.admin.socialPublish.title')}</span>
                  </div>
                  <ArrowRightIcon className="w-5 h-5" />
                </div>
              </Link>

              {/* Bouton Import depuis site web d'une commune */}
              <Link to="/admin/import-commune" className="block bg-white p-4 rounded-lg shadow-sm">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <span className="mr-3">🏛️</span>
                    <span>{t('settings.admin.importCommune.title')}</span>
                  </div>
                  <ArrowRightIcon className="w-5 h-5" />
                </div>
              </Link>
              {/* Bouton Import depuis site web */}
              <Link to="/admin/import-website" className="block bg-white p-4 rounded-lg shadow-sm">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <span className="mr-3">🌐</span>
                    <span>{t('settings.admin.importWebsite.title')}</span>
                  </div>
                  <ArrowRightIcon className="w-5 h-5" />
                </div>
              </Link>

              {/* Bouton Gestion des utilisateurs */}
              <Link to="/admin/users" className="block bg-white p-4 rounded-lg shadow-sm">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <span className="mr-3">👥</span>
                    <span>{t('settings.admin.users.title')}</span>
                  </div>
                  <ArrowRightIcon className="w-5 h-5" />
                </div>
              </Link>

              {/* Bouton Paramètres généraux */}
              <Link to="/admin/general-settings" className="block bg-white p-4 rounded-lg shadow-sm">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <span className="mr-3">⚙️</span>
                    <span>{t('settings.admin.generalSettings.title')}</span>
                  </div>
                  <ArrowRightIcon className="w-5 h-5" />
                </div>
              </Link>

              {/* Bouton Gestion des catégories */}
              <Link to="/admin/categories" className="block bg-white p-4 rounded-lg shadow-sm">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <span className="mr-3">🎨</span>
                    <span>{t('settings.admin.categories.title')}</span>
                  </div>
                  <ArrowRightIcon className="w-5 h-5" />
                </div>
              </Link>

              {/* Bouton Test IA */}
              <Link to="/admin/test-ia" className="block bg-white p-4 rounded-lg shadow-sm">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <span className="mr-3">🤖</span>
                    <span>{t('settings.admin.testIa.title')}</span>
                  </div>
                  <ArrowRightIcon className="w-5 h-5" />
                </div>
              </Link>
              
              {/* Bouton Aperçu des icônes */}
              <Link to="/admin/icons-preview" className="block bg-white p-4 rounded-lg shadow-sm">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <span className="mr-3">🎨</span>
                    <span>{t('settings.admin.iconsPreview.title')}</span>
                  </div>
                  <ArrowRightIcon className="w-5 h-5" />
                </div>
              </Link>


              {/* Bouton Géocodage des événements */}
              <Link to="/admin/geo-event" className="block bg-white p-4 rounded-lg shadow-sm">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <span className="mr-3">🌍</span>
                    <span>{t('settings.admin.geoEvent.title')}</span>
                  </div>
                  <ArrowRightIcon className="w-5 h-5" />
                </div>
              </Link>

              {/* Bouton Batchs OpenAI */}
              <Link to="/admin/openai-batches" className="block bg-white p-4 rounded-lg shadow-sm mb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <span className="mr-3">🤖</span>
                    <span>{t('settings.admin.openAIBatches.title')}</span>
                  </div>
                  <ArrowRightIcon className="w-5 h-5" />
                </div>
              </Link>

              {/* Bouton Mise à jour des communes */}
              <Link to="/admin/update-commune" className="block bg-white p-4 rounded-lg shadow-sm mb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <span className="mr-3">🏘️</span>
                    <span>{t('settings.admin.updateCommune.title')}</span>
                  </div>
                  <ArrowRightIcon className="w-5 h-5" />
                </div>
              </Link>



            </div>
          </section>
        )}

        {/* Section Aide et support */}
        <section className="mb-8">
          <h2 className="text-h2 mb-4">{t('settings.help.title')}</h2>
          
          <Link to="/settings/contact" className="block bg-white p-4 rounded-lg shadow-sm mb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <MailIcon className="w-6 h-6 mr-3" />
                <span>{t('settings.help.contact')}</span>
              </div>
              <ArrowRightIcon className="w-5 h-5" />
            </div>
          </Link>
          

                    
          <Link to="/settings/legal" className="block bg-white p-4 rounded-lg shadow-sm mb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <LegalIcon className="w-6 h-6 mr-3" />
                <span>{t('settings.help.legal')}</span>
              </div>
              <ArrowRightIcon className="w-5 h-5" />
            </div>
          </Link>
          
          <Link to="/settings/terms" className="block bg-white p-4 rounded-lg shadow-sm mb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <TermsIcon className="w-6 h-6 mr-3" />
                <span>{t('settings.help.terms')}</span>
              </div>
              <ArrowRightIcon className="w-5 h-5" />
            </div>
          </Link>

          <Link to="/settings/privacy" className="block bg-white p-4 rounded-lg shadow-sm mb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <PrivacyIcon className="w-6 h-6 mr-3" />
                <span>{t('settings.help.privacy')}</span>
              </div>
              <ArrowRightIcon className="w-5 h-5" />
            </div>
          </Link>
        
        {/* Crédits */}
          <Link to="/settings/credits-page" className="block bg-white p-4 rounded-lg shadow-sm mb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <CopyrightIcon className="w-6 h-6 mr-3" />
                <span>{t('settings.help.credits')}</span>
              </div>
              <ArrowRightIcon className="w-5 h-5" />
            </div>
          </Link>
        {/* Soutenir le projet via un don */}
        <a 
            href="https://buy.stripe.com/4gM5kDfgbe0j0Tvh2tefC00" 
            target="_blank" 
            rel="noopener noreferrer"
            className="block bg-white p-4 rounded-lg shadow-sm mb-4"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <HandHeartIcon className="w-6 h-6 mr-3" />
                <span>{t('settings.help.donate')}</span>
              </div>
              <ArrowRightIcon className="w-5 h-5" />
            </div>
          </a>
        </section>

        {/* Section Application */}
        <section className="mb-8">
          <h2 className="text-h2 mb-4">{t('settings.app.title')}</h2>
          
          {/* Instructions spécifiques pour iOS */}
          {isIOS && !isInstalled && (
            <div className="bg-blue-50 p-4 rounded-lg shadow-sm mb-4">
              <div className="flex items-start">
                <SmartphoneIcon className="w-6 h-6 mr-3 flex-shrink-0 mt-1" />
                <div className="flex-1">
                  <p className="font-medium mb-2">{t('settings.app.iosInstallTitle')}</p>
                  {isSafari ? (
                    <ol className="list-decimal list-inside text-sm text-gray-700 space-y-1">
                      <li>{t('settings.app.iosInstallStep1')}</li>
                      <li>{t('settings.app.iosInstallStep2')}</li>
                      <li>{t('settings.app.iosInstallStep3')}</li>
                    </ol>
                  ) : (
                    <div className="text-sm text-gray-700">
                      <p className="mb-2">{t('settings.app.iosInstallChrome')}</p>
                      <p className="font-medium">{t('settings.app.iosInstallOpenSafari')}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Afficher le bouton d'installation si disponible et non installé (pas sur iOS) */}
          {!isIOS && !isInstalled && deferredPrompt && (
            <button 
              onClick={handleInstallClick}
              className="block bg-white p-4 rounded-lg shadow-sm mb-4 w-full text-left"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <SmartphoneIcon className="w-6 h-6 mr-3" />
                  <span>{t('settings.app.installApp')}</span>
                </div>
                <ArrowRightIcon className="w-5 h-5" />
              </div>
            </button>
          )}

          {/* Afficher un message si déjà installé */}
          {isInstalled && (
            <div className="bg-white p-4 rounded-lg shadow-sm mb-4">
              <div className="flex items-center">
                <SmartphoneAppIcon className="w-6 h-6 mr-3" />
                <span className="text-gray-600">{t('settings.app.alreadyInstalled')}</span>
              </div>
            </div>
          )}

          {/* Message de débogage si le prompt n'est pas disponible et l'app n'est pas installée (pas sur iOS) */}
          {/* {!isIOS && !isInstalled && !deferredPrompt && (
            <div className="bg-blue-50 p-4 rounded-lg shadow-sm mb-4">
              <div className="flex items-center">
                <span className="mr-3">ℹ️</span>
                <div className="text-sm text-gray-700">
                  <p className="font-medium">{t('settings.app.installInfo')}</p>
                  <ul className="list-disc list-inside mt-2 text-xs">
                    <li>{t('settings.app.installCondition1')}</li>
                    <li>{t('settings.app.installCondition2')}</li>
                    <li>{t('settings.app.installCondition3')}</li>
                  </ul>
                </div>
              </div>
            </div>
          )} */}
          
          <div className="bg-white p-4 rounded-lg shadow-sm">
            <LanguageSwitcher />
          </div>
        </section>

        {/* Version de l'application */}
        <p className="text-center text-gray-400 text-xs mt-8">
          {t('app.version')}
        </p>
      </div>

      {/* Modales d'authentification */}
      <LoginModal 
        isOpen={showLoginModal}
        onClose={handleCloseAllModals}
        onSwitchToRegister={handleOpenRegisterModal}
        onSwitchToForgotPassword={handleOpenForgotPasswordModal}
      />
      
      <RegisterModal 
        isOpen={showRegisterModal}
        onClose={handleCloseAllModals}
        onSwitchToLogin={handleOpenLoginModal}
      />
      
      <ForgotPasswordModal 
        isOpen={showForgotPasswordModal}
        onClose={handleCloseAllModals}
        onSwitchToLogin={handleOpenLoginModal}
      />
    </div>
  );
};


export default SettingsPage;
